import React from "react";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { INPUT_BOX_CLASS, INPUT_BOX_STYLE } from "./checkerFieldStyles";
import { ChoiceGroup, YesNoChoice } from "./ChoiceCard";
import FieldError from "./FieldError";
import GlossaryHint from "./GlossaryHint";
import { fieldAnchorProps, invalidInputProps } from "./checkerValidation";

/** Plain-language definitions for the funding terms that have no obvious meaning on their own. */
const FUNDING_GLOSSARY = {
  "Capital preference":
    "Non-dilutive only: grants, subsidies or tax incentives where you give up no equity. Equity: investment in exchange for shares. Debt / financing: loans or credit you repay. Blended / flexible: open to a mix of these.",
  "Business model":
    "How you sell. Deeptech / IP-led means the value sits in proprietary technology or research (patents, algorithms, novel hardware) rather than in distribution or service delivery. SaaS is software sold on subscription; Marketplace connects buyers and sellers.",
  "Company category":
    "SME: an established company with revenue (typically under S$100M turnover or 200 staff). Startup: early-stage, usually pre-profit. Scale-up: proven model, growing fast. Social enterprise: impact is the primary aim. Founder team: not incorporated yet. Research spinout: born out of a university or institute.",
};

const fundingGoalOptions = [
  { value: "digital_transformation", label: "Digital transformation" },
  { value: "working_capital", label: "Working capital" },
  { value: "product_build", label: "Product build / R&D" },
  { value: "pilot", label: "Pilot deployment" },
  { value: "regional_expansion", label: "Regional expansion" },
  { value: "impact_scale", label: "Impact scale" },
  { value: "market_validation", label: "Market validation" },
];

const fundingPreferenceOptions = [
  { value: "non_dilutive", label: "Non-dilutive only" },
  { value: "equity", label: "Equity" },
  { value: "debt", label: "Debt / financing" },
  { value: "blended", label: "Blended / flexible" },
];

const revenueStatusOptions = [
  { value: "pre_revenue", label: "Pre-revenue" },
  { value: "early_revenue", label: "Early revenue" },
  { value: "revenue_generating", label: "Revenue generating" },
  { value: "profitable", label: "Profitable" },
];

const businessModelOptions = [
  { value: "b2b", label: "B2B" },
  { value: "b2c", label: "B2C" },
  { value: "saas", label: "SaaS" },
  { value: "enterprise", label: "Enterprise sales" },
  { value: "marketplace", label: "Marketplace" },
  { value: "deeptech", label: "Deeptech / IP-led" },
  { value: "subscriptions", label: "Subscription / recurring revenue" },
];

const entityTypeOptions = [
  { value: "sme", label: "SME" },
  { value: "startup", label: "Startup" },
  { value: "social_enterprise", label: "Social enterprise / impact venture" },
  { value: "scaleup", label: "Scale-up" },
  { value: "founder_team", label: "Founder team / pre-company" },
  { value: "research_spinout", label: "Research spinout" },
];

const deploymentEnvironmentOptions = [
  { value: "internal_operations", label: "Internal operations" },
  { value: "customer_deployment", label: "Customer deployment" },
  { value: "sector_pilot", label: "Sector pilot / live use case" },
  { value: "infrastructure_site", label: "Infrastructure / site environment" },
  { value: "overseas_market", label: "Overseas market rollout" },
];

const useOfFundsOptions = [
  { value: "software_tools", label: "Software / tools" },
  { value: "equipment_hardware", label: "Equipment / hardware" },
  { value: "consultancy_services", label: "Consultancy / services" },
  { value: "hiring_talent", label: "Hiring / talent build" },
  { value: "pilot_delivery", label: "Pilot delivery" },
  { value: "working_capital", label: "Working capital" },
];

const targetCustomerOptions = [
  { value: "sme_clients", label: "SME clients" },
  { value: "enterprise_clients", label: "Enterprise clients" },
  { value: "public_sector", label: "Public sector / government buyers" },
  { value: "healthcare_institutions", label: "Healthcare institutions" },
  { value: "construction_infrastructure", label: "Construction / infrastructure operators" },
  { value: "consumers", label: "Consumers" },
];

const tractionStageOptions = [
  { value: "no_customers_yet", label: "No active customers yet" },
  { value: "active_pilots", label: "Active pilots / trials" },
  { value: "paying_customers", label: "Paying customers" },
  { value: "recurring_revenue", label: "Recurring revenue base" },
  { value: "strategic_partners", label: "Strategic partners / channels" },
];

const marketScopeOptions = [
  { value: "singapore_only", label: "Singapore only" },
  { value: "singapore_plus_asean", label: "Singapore + ASEAN" },
  { value: "overseas_expansion", label: "Broader overseas expansion" },
];

const applicationReadinessOptions = [
  { value: "ready_to_apply", label: "Ready to apply now" },
  { value: "needs_refinement", label: "Needs refinement first" },
  { value: "exploratory", label: "Exploratory / early-stage" },
];

function renderSelectedLabel(value, options, placeholder) {
  const match = options.find((option) => option.value === value);
  return match ? match.label : placeholder;
}

const COMPACT_TRIGGER_CLASS = `${INPUT_BOX_CLASS} !h-11 min-h-[44px]`;

function SelectField({
  name,
  label,
  value,
  onChange,
  options,
  placeholder,
  helper,
  glossaryTerm,
  error,
  prominent = false,
}) {
  const labelId = `${name}-label`;
  const helperId = helper ? `${name}-helper` : "";
  return (
    <div className="space-y-1.5" {...fieldAnchorProps(name)}>
      <div className="flex items-center gap-2">
        <Label
          id={labelId}
          htmlFor={name}
          className={prominent ? "font-semibold" : "font-medium"}
          style={{ color: "#4B5563" }}
        >
          {label} <span style={{ color: "#4B5563" }}>*</span>
        </Label>
        {glossaryTerm && FUNDING_GLOSSARY[glossaryTerm] ? (
          <GlossaryHint term={glossaryTerm} definition={FUNDING_GLOSSARY[glossaryTerm]} />
        ) : null}
      </div>
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger
          id={name}
          aria-labelledby={labelId}
          className={COMPACT_TRIGGER_CLASS}
          style={INPUT_BOX_STYLE}
          {...invalidInputProps(name, error, helperId)}
        >
          <SelectValue placeholder={placeholder}>
            {renderSelectedLabel(value, options, placeholder)}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {helper ? (
        <p id={helperId} className="text-xs leading-relaxed" style={{ color: "#94A3B8" }}>
          {helper}
        </p>
      ) : null}
      <FieldError name={name} message={error} />
    </div>
  );
}

export default function FundingStrategyStep({ formData, updateFormData, errors = {} }) {
  return (
    <div id="step-funding-strategy" className="space-y-10">
      <div className="text-center mb-10">
        <h2 className="text-3xl font-bold mb-3" style={{ color: "#4B5563" }} tabIndex={-1} data-step-heading>Funding Strategy</h2>
        <p className="text-lg max-w-2xl mx-auto" style={{ color: "#4B5563" }}>
          A few questions about what the money is for and how your company operates, so we can check grants alongside other funding routes.
        </p>
      </div>

      <div className="w-full rounded-3xl bg-white/5 backdrop-blur-xl shadow-2xl border border-white/10 p-6 md:p-8">
        <div className="mb-6">
          <h3 className="text-xl font-semibold" style={{ color: "#4B5563" }}>Funding profile</h3>
          <p className="text-sm mt-1" style={{ color: "#4B5563" }}>
            Your answers here are also used to check impact funding, accelerators, investment and financing options, not only grants.
          </p>
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl bg-white/90 p-5 shadow-[0_18px_45px_rgba(15,23,42,0.08)] md:p-6">
            <div className="mb-4">
              <h4 className="text-base font-semibold" style={{ color: "#4B5563" }}>What the funding is for</h4>
              <p className="text-xs mt-1" style={{ color: "#94A3B8" }}>Tell us what you want the money to do and what kind of funding you would accept.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <SelectField
                name="funding_goal"
                label="Primary Funding Goal"
                value={formData.funding_goal}
                onChange={(value) => updateFormData({ funding_goal: value })}
                options={fundingGoalOptions}
                placeholder="Select funding goal"
                helper="Sets the primary route OPTRA prioritizes first, especially when the project is internal digital transformation or workforce uplift rather than a client-facing product build."
                error={errors.funding_goal}
                prominent
              />
              <SelectField
                name="funding_preference"
                label="Capital Preference"
                value={formData.funding_preference}
                onChange={(value) => updateFormData({ funding_preference: value })}
                options={fundingPreferenceOptions}
                placeholder="Select capital preference"
                helper="Tells us whether to weigh grants against equity or financing routes."
                glossaryTerm="Capital preference"
                error={errors.funding_preference}
                prominent
              />
              <div className="md:col-span-2">
                <SelectField
                  name="primary_use_of_funds"
                  label="Primary Use Of Funds"
                  value={formData.primary_use_of_funds}
                  onChange={(value) => updateFormData({ primary_use_of_funds: value })}
                  options={useOfFundsOptions}
                  placeholder="Select use of funds"
                  error={errors.primary_use_of_funds}
                />
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-white/90 p-5 shadow-[0_18px_45px_rgba(15,23,42,0.08)] md:p-6">
            <div className="mb-4">
              <h4 className="text-base font-semibold" style={{ color: "#4B5563" }}>About the company</h4>
              <p className="text-xs mt-1" style={{ color: "#94A3B8" }}>The basics that decide which schemes a company like yours can apply to.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <SelectField
                name="eligible_entity_type"
                label="Company category"
                value={formData.eligible_entity_type}
                onChange={(value) =>
                  updateFormData({
                    eligible_entity_type: value,
                    companyType: value,
                  })
                }
                options={entityTypeOptions}
                placeholder="Select company category"
                helper="Not the legal entity type from section 1: this is the stage and kind of company you are."
                glossaryTerm="Company category"
                error={errors.eligible_entity_type}
              />
              <SelectField
                name="business_model"
                label="Business Model"
                value={formData.business_model}
                onChange={(value) => updateFormData({ business_model: value })}
                options={businessModelOptions}
                placeholder="Select business model"
                helper="Used to match you to suitable grant and non-grant routes."
                glossaryTerm="Business model"
                error={errors.business_model}
                prominent
              />
              <SelectField
                name="revenue_status"
                label="Revenue Status"
                value={formData.revenue_status}
                onChange={(value) => updateFormData({ revenue_status: value })}
                options={revenueStatusOptions}
                placeholder="Select revenue stage"
                error={errors.revenue_status}
              />
              <SelectField
                name="target_customer_type"
                label="Target Buyer / Customer"
                value={formData.target_customer_type}
                onChange={(value) => updateFormData({ target_customer_type: value })}
                options={targetCustomerOptions}
                placeholder="Select buyer type"
                error={errors.target_customer_type}
              />
            </div>
          </div>

          <div className="rounded-2xl bg-white/90 p-5 shadow-[0_18px_45px_rgba(15,23,42,0.08)] md:p-6">
            <div className="mb-4">
              <h4 className="text-base font-semibold" style={{ color: "#4B5563" }}>Where it will be used and how far along you are</h4>
              <p className="text-xs mt-1" style={{ color: "#94A3B8" }}>Where the project will run and whether you already have customers.</p>
            </div>
            <div className="space-y-4">
              <SelectField
                name="deployment_environment"
                label="Deployment Environment"
                value={formData.deployment_environment}
                onChange={(value) => updateFormData({ deployment_environment: value })}
                options={deploymentEnvironmentOptions}
                placeholder="Select deployment environment"
                error={errors.deployment_environment}
              />
              <SelectField
                name="traction_stage"
                label="Current Traction"
                value={formData.traction_stage}
                onChange={(value) => updateFormData({ traction_stage: value })}
                options={tractionStageOptions}
                placeholder="Select traction stage"
                error={errors.traction_stage}
              />
            </div>
          </div>

          <div className="rounded-2xl bg-white/90 p-5 shadow-[0_18px_45px_rgba(15,23,42,0.08)] md:p-6">
            <div className="mb-4">
              <h4 className="text-base font-semibold" style={{ color: "#4B5563" }}>Markets and readiness</h4>
              <p className="text-xs mt-1" style={{ color: "#94A3B8" }}>Which markets you are aiming at and how ready you are to apply.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <SelectField
                  name="market_scope"
                  label="Market Scope"
                  value={formData.market_scope}
                  onChange={(value) => updateFormData({ market_scope: value })}
                  options={marketScopeOptions}
                  placeholder="Select market scope"
                  error={errors.market_scope}
                />
              </div>
              <div className="md:col-span-2">
                <SelectField
                  name="application_readiness"
                  label="Application Readiness"
                  value={formData.application_readiness}
                  onChange={(value) => updateFormData({ application_readiness: value })}
                  options={applicationReadinessOptions}
                  placeholder="Select readiness level"
                  helper="Helps us judge how confident the recommendations can be."
                  error={errors.application_readiness}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6 mt-8">
          <ChoiceGroup
            name="vc_backed"
            legend="VC-backed status"
            required
            error={errors.vc_backed}
          >
            <YesNoChoice
              name="vc_backed"
              value={formData.vc_backed}
              onChange={(next) => updateFormData({ vc_backed: next })}
              yesLabel="VC-backed"
              yesDescription="We already have institutional or venture backing."
              noLabel="Not VC-backed"
              noDescription="No institutional or venture backing today."
            />
          </ChoiceGroup>

          <ChoiceGroup
            name="pilot_ready"
            legend="Pilot readiness"
            required
            error={errors.pilot_ready}
          >
            <YesNoChoice
              name="pilot_ready"
              value={formData.pilot_ready}
              onChange={(next) => updateFormData({ pilot_ready: next })}
              yesLabel="Pilot-ready soon"
              yesDescription="We can support pilot deployment or challenge participation in the near term."
              noLabel="Not pilot-ready yet"
              noDescription="We are earlier-stage and still building readiness."
            />
          </ChoiceGroup>

          <ChoiceGroup
            name="impact_intent"
            legend="Impact Intent"
            required
            error={errors.impact_intent}
          >
            <YesNoChoice
              name="impact_intent"
              value={formData.impact_intent}
              onChange={(next) => updateFormData({ impact_intent: next })}
              yesLabel="Impact-driven mission"
              yesDescription="Social, climate, community, or mission-led outcomes are central to this company."
              noLabel="Commercial-first mission"
              noDescription="Commercial growth is primary, with impact outcomes secondary or indirect."
            />
          </ChoiceGroup>
        </div>
      </div>
    </div>
  );
}
